import { integrationTesting } from '@ps/web-core-utils';
import { useNavigate } from '@ps/web-shared-components';
import { useSelector } from 'react-redux';

import { Fork, Invoke, nextPointer } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import { select } from '@swf/core/store';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import SignupBlockedCountry from '../components/SignupBlockedCountry/SignupBlockedCountry';
import SignupCountry from '../components/SignupCountry/SignupCountry';
import SignupEmail from '../components/SignupEmail/SignupEmail';
import SignupLegal from '../components/SignupLegal/SignupLegal';
import SignupOffer from '../components/SignupOffer/SignupOffer';
import SignupPassword from '../components/SignupPassword/SignupPassword';
import SignupUsername from '../components/SignupUsername/SignupUsername';

import { Guard } from './useSignupGuards';

const successDelay = integrationTesting ? 0 : 3000;
const signupRegisterDelay = {
  name: 'signupRegisterDelay',
  after: { [successDelay]: nextPointer }
};
const useMultiScreenSignupFlow = (): FlowConfig => {
  const navigate = useNavigate();
  const exitWizard = useWizardExit();
  const isPhone = useSelector(select.device.isPhone);

  const forkCountryAvailable = new Fork(
    Guard.SignupCountryAvailable,
    [SignupUsername],
    [SignupBlockedCountry]
  );

  const forkProvinceCheck = new Fork(
    Guard.ProvinceCheck,
    [
      {
        name: 'exitProvince',
        entry: ({ values: { country, state }, data: { redirectUrl } }) => {
          const url = new URL(redirectUrl.url);
          if (!isPhone) {
            Object.entries({ country, state }).forEach(([key, value]) =>
              url.searchParams.append(key, value)
            );
          }
          window.location.href = url.toString();
        },
        type: 'final'
      }
    ],
    [forkCountryAvailable, SignupCountry]
  );

  const invokeSignupCountryCheck = new Invoke(
    'signupCountryCheck',
    forkProvinceCheck,
    SignupCountry
  );

  const invokeSetCountry = new Invoke('setCountry', invokeSignupCountryCheck);

  const invokeSignupRegister = new Invoke('signupRegister', signupRegisterDelay, SignupLegal);

  const forkSkipSignupCountry = new Fork(
    Guard.SkipSignupCountry,
    [invokeSetCountry],
    [SignupCountry]
  );

  const forkAuthenticationCheck = new Fork(
    Guard.AuthenticationCheck,
    [{ name: 'exitSignup', entry: exitWizard, type: 'final' }],
    [forkSkipSignupCountry, SignupCountry]
  );

  const forkCompleteSignup = new Fork(
    Guard.AutoTrigerRmf,
    [{ name: 'enterRMF', entry: () => navigate('/account/cashier/rmf'), type: 'final' }],
    [SignupOffer]
  );
  const forkRmfcheck = new Fork(
    Guard.SignupRmfEnabled,
    [{ name: 'signupExit', entry: Action.GoNext }],
    [forkCompleteSignup]
  );
  return [
    forkAuthenticationCheck,
    invokeSignupCountryCheck,
    forkProvinceCheck,
    forkCountryAvailable,
    SignupPassword,
    SignupEmail,
    SignupLegal,
    invokeSignupRegister,
    signupRegisterDelay,
    forkRmfcheck
  ];
};

export default useMultiScreenSignupFlow;
